/**
 * 設定ページ
 *
 * ユーザーのアプリ設定を管理する画面。
 * 現在はタイムゾーン設定のみ。
 *
 * Rails で言えば:
 * - app/controllers/settings_controller.rb#show のビューに相当
 * - データは Inertia.js 経由で props として受け取る
 */

import { useForm, Head, Link } from '@inertiajs/react'
import type { FormEvent } from 'react'
import { HabitLayout } from '@/components/habits/HabitLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { settings_path, settings_api_keys_path } from '@/lib/routes'
import type { PageProps } from '@/types'

interface TimezoneOption {
  value: string
  label: string
  disabled?: boolean
}

interface SettingsProps extends PageProps {
  timezones: TimezoneOption[]
  currentTimezone: string | null
  loggingEnabled: boolean
}

const DEFAULT_TIMEZONE = 'Asia/Tokyo'

export default function SettingsIndex({
  timezones,
  currentTimezone,
  loggingEnabled,
}: SettingsProps) {
  const { data, setData, patch, processing } = useForm({
    settings: {
      timezone: currentTimezone ?? DEFAULT_TIMEZONE,
      logging_enabled: loggingEnabled,
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    patch(settings_path())
  }

  return (
    <>
      <Head title="設定 | Init Habit" />

      <HabitLayout title="設定">
        <div className="mx-auto max-w-2xl">
          <div className="bg-card border-border rounded-xl border p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="timezone" className="text-base font-medium">
                  タイムゾーン
                </Label>
                <p className="text-muted-foreground mb-3 text-sm">
                  タイムゾーンを選択してください。習慣の「今日」の判定に使用されます。
                </p>
                <select
                  id="timezone"
                  value={data.settings.timezone}
                  onChange={(e) =>
                    setData('settings', {
                      ...data.settings,
                      timezone: e.target.value,
                    })
                  }
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {timezones.map((tz, index) =>
                    tz.disabled ? (
                      <option key={index} disabled>
                        {tz.label}
                      </option>
                    ) : (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <Label className="text-base font-medium">ログ記録</Label>
                <p className="text-muted-foreground mb-3 text-sm">
                  習慣の完了時に、実施時刻・所要時間・メモを記録するモーダルを表示します。
                  各習慣の設定で個別にオーバーライドできます。
                </p>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={data.settings.logging_enabled}
                    onChange={(e) =>
                      setData('settings', {
                        ...data.settings,
                        logging_enabled: e.target.checked,
                      })
                    }
                    className="h-5 w-5 rounded"
                  />
                  <span className="text-sm">ログ記録を有効にする</span>
                </label>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={processing}>
                  {processing ? '保存中...' : '保存'}
                </Button>
              </div>
            </form>
          </div>

          {/* APIキー管理 */}
          <div className="bg-card border-border mt-6 rounded-xl border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium">APIキー</h3>
                <p className="text-muted-foreground text-sm">
                  ChatGPTsなどの外部サービスとの連携に使用します
                </p>
              </div>
              <Link href={settings_api_keys_path()}>
                <Button variant="outline">管理</Button>
              </Link>
            </div>
          </div>
        </div>
      </HabitLayout>
    </>
  )
}
